<template>
  <div class="categories">
    <div class="categories__container">
      <div class="categories__content" :class="{ 'categories__content--loading': loading }">
        <template v-if="loading">
          <div :class="['p-breadcrumb', loading && 'skeleton-wrapper']">
            <div
              v-for="i in 2"
              :key="`skeleton-breadcrumb-${i}`"
              class="p-breadcrumb__item d-flex align-items-center gap-8"
            >
              <div
                class="categories__skeleton-line categories__skeleton-line--breadcrumb f-utility-14-medium"
              ></div>
            </div>
          </div>
        </template>
        <p-breadcrumb
          v-else-if="categories"
          :items="[
            { label: $t('general.home'), to: '/' },

            ...(categories?.categories || []).map((category, index) => {
              let link = category.slug
              if (0 < index && categories?.categories?.[0]) {
                link = `${categories.categories[0].slug}/${category.slug}`
              }
              return {
                label: category.title,
                to: appLink(link),
              }
            }),
            { label: categories?.title || '', to: '' },
          ]"
        />

        <div v-if="$device.isDesktop" class="mt-40">
          <nuxt-img src="/icon/paribu-color.svg" width="116" height="24" />
          <div class="f-utility-14 t-fg-secondary mt-12">
            {{ $t('general.cryptocurrencyTrading') }}
          </div>
        </div>
        <div class="row mt-40">
          <div class="col-12 col-md-4">
            <template v-if="$device.isDesktop">
              <template v-if="loading">
                <div :class="['d-flex flex-column gap-16', loading && 'skeleton-wrapper']">
                  <div
                    v-for="i in 6"
                    :key="`skeleton-sidebar-${i}`"
                    class="d-flex align-items-center gap-12 py-16"
                  >
                    <div
                      class="categories__skeleton-circle categories__skeleton-circle--small"
                    ></div>
                    <div
                      class="categories__skeleton-line categories__skeleton-line--sidebar f-utility-14-medium"
                    ></div>
                  </div>
                </div>
              </template>
              <template v-else>
                <p-root-categories-sidebar
                  v-for="rootCategory in rootCategories"
                  :key="rootCategory.id"
                  :icon="rootCategory.image_url"
                  :title="rootCategory.title"
                  :link="appLink(rootCategory.slug)"
                  @click="page.type = 'category'"
                />
              </template>
            </template>
            <template v-else>
              <p-select
                v-model="selectedCategorySlug"
                :name="'mobile-root-category'"
                :label="selectedCategoryLabel || selectItems[0]?.label || '-'"
                :items="selectItems"
                readonly
                class="mb-32"
                @on-change="handleMobileSelect"
              />
            </template>
          </div>

          <div class="col-12 col-md-8">
            <template v-if="loading">
              <div :class="['d-flex flex-column gap-24', loading && 'skeleton-wrapper']">
                <div
                  class="categories__skeleton-line categories__skeleton-line--title f-title-32 mb-40"
                ></div>
                <div class="d-flex flex-column gap-20">
                  <div
                    v-for="i in 4"
                    :key="`skeleton-category-${i}`"
                    class="d-flex flex-column gap-16"
                  >
                    <div
                      class="categories__skeleton-line categories__skeleton-line--subtitle f-utility-16-medium"
                    ></div>
                    <ul class="categories__group--list d-flex flex-column gap-4">
                      <li
                        v-for="j in 3"
                        :key="`skeleton-item-${i}-${j}`"
                        class="categories__skeleton-line categories__skeleton-line--item f-utility-14"
                      ></li>
                    </ul>
                  </div>
                </div>
              </div>
            </template>
            <template v-else-if="categories">
              <p-content-title :title="categories.title" :social-icons="[]" class="mb-40" />

              <div class="categories__group--root"></div>
              <div
                v-for="category in categories.items"
                :key="`${categories.id}--${category.id}`"
                :class="
                  category.type === 'content' ? 'home__popular-articles' : 'categories__group'
                "
              >
                <NuxtLink
                  v-if="category.type === 'category'"
                  :to="appLink(`${categories.slug}/${category.slug}`)"
                  @click="page.type = 'category'"
                >
                  <p-page-subtitle :title="category.title" class="mb-16" />
                </NuxtLink>
                <p-article-link
                  v-else-if="category.type === 'content'"
                  size="md"
                  :label="category.title"
                  :to="appLink(`${categories.slug}/${category.slug}`)"
                  class="home__popular-articles-item"
                  @click="page.type = 'content'"
                />
                <ul
                  v-if="category.items && category.items.length > 0"
                  class="categories__group--list"
                >
                  <li
                    v-for="item in category.items"
                    :key="`${categories.id}--${category.id}--${item.id}`"
                  >
                    <p-article-link
                      size="md"
                      :label="item.title"
                      :to="appLink(`${categories.slug}/${item.slug}`)"
                      class="home__popular-articles-item"
                      @click="page.type = 'content'"
                    />
                  </li>
                </ul>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'

import { useAppLink } from '@/composables/useAppLink'
import { useSupportStore } from '@/store/support'
import { usePageStore } from '@/store/page'

interface Seo {
  index?: boolean
  meta_title?: string
  meta_description?: string
}

interface Category {
  id: string
  title: string
  slug: string
  type?: string
  image_url?: string
  seo?: Seo
  items?: Category[]
  categories?: Category[]
}

const { categories, loading } = defineProps<{
  categories: Category | null
  loading: boolean
}>()

// SEO meta tags
useHead({
  title: computed(() => categories?.seo?.meta_title || categories?.title || ''),
  meta: computed(() => {
    const meta: Array<{ name: string; content: string; key?: string }> = []

    const description = categories?.seo?.meta_description
    if (description) {
      meta.push({ name: 'description', content: description, key: 'description' })
    }

    const robotsContent = categories?.seo?.index === false ? 'noindex, nofollow' : 'index, follow'
    meta.push({ name: 'robots', content: robotsContent, key: 'robots' })

    return meta
  }),
})

const route = useRoute()
const { appLink } = useAppLink()
const { page } = usePageStore()

// JSON-LD: BreadcrumbList (SEO structured data)
const requestUrl = useRequestURL()
const breadcrumbJsonLd = computed(() => {
  const items: Array<{
    '@type': 'ListItem'
    position: number
    name: string
    item: string
  }> = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Anasayfa',
      item: `${requestUrl.origin}/destek/`,
    },
  ]

  const parents = categories?.categories || []
  parents.forEach((parent, index) => {
    let link = parent.slug || ''
    if (index > 0 && parents[0]?.slug) link = `${parents[0].slug}/${parent.slug}`
    items.push({
      '@type': 'ListItem',
      position: items.length + 1,
      name: parent.title,
      item: `${requestUrl.origin}${appLink(link)}`,
    })
  })

  if (categories?.title) {
    items.push({
      '@type': 'ListItem',
      position: items.length + 1,
      name: categories.title,
      item: `${requestUrl.origin}${route.path}`,
    })
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  }
})

const safeJsonLd = (obj: unknown) => JSON.stringify(obj).replace(/</g, '\\u003c')

useHead({
  script: computed(() => [
    {
      type: 'application/ld+json',
      innerHTML: safeJsonLd(breadcrumbJsonLd.value),
      key: 'jsonld-breadcrumb',
    },
  ]),
})

const supportStore = useSupportStore()
const { rootList: rootCategories } = storeToRefs(supportStore)
supportStore.fetchSupportCategories()

const selectItems = computed(
  () =>
    rootCategories.value?.map((cat) => ({
      label: cat.title,
      value: cat.slug,
    })) || [],
)
const selectedCategorySlug = ref('')
const selectedCategoryLabel = computed(() => {
  const byValue = selectItems.value.find((item) => item.value === selectedCategorySlug.value)?.label
  return byValue || selectedCategorySlug.value || ''
})

const handleMobileSelect = (item: { label: string; value: string }) => {
  page.type = 'category'
  navigateTo(appLink(item.value))
}

watch(
  () => categories,
  (newVal) => {
    if (newVal?.title) {
      selectedCategorySlug.value = newVal.title || ''
    }
  },
  {
    immediate: true,
  },
)

definePageMeta({
  layout: 'default',
})
</script>
